import type { AxiosResponse } from 'axios'
import { apiClient } from '@/api/api'
import {
  normalizeConsentsCurrent,
  type ConsentsAcceptRequest,
  type ConsentsCurrent,
} from '../consents.data'

/**
 * Account lifecycle and consents (fair-api `docs/release_journal/acct.md` § FE contract 16 / 18).
 * Restore and resend are public: the restore link carries the token, the scheduled account has
 * no usable bearer.
 */

export interface AccountRestoreResponse {
  status: 'restored'
  signInRequired: boolean
}

/** `POST /account/restore {token}` → 200; 401 / 410 / 409 / 503 → `accountRestoreErrorOf`. */
export const restoreAccount = (token: string): Promise<AxiosResponse<AccountRestoreResponse>> =>
  apiClient.post<AccountRestoreResponse>('/account/restore', { token })

/** `POST /account/restore/resend {email}` → always 202 (no address is confirmed or denied). */
export const resendAccountRestore = (email: string): Promise<AxiosResponse<void>> =>
  apiClient.post<void>('/account/restore/resend', { email: email.trim() })

/** `GET /consents/current` — bearer optional; a guest gets every kind in `mustAccept`. */
export const getConsentsCurrent = async (): Promise<ConsentsCurrent> => {
  const response = await apiClient.get<unknown>('/consents/current')
  return normalizeConsentsCurrent(response.data)
}

/** `POST /consents/accept` (bearer) → the registry after recording; 409 = stale version. */
export const acceptConsents = async (body: ConsentsAcceptRequest): Promise<ConsentsCurrent> => {
  const response = await apiClient.post<unknown>('/consents/accept', body)
  return normalizeConsentsCurrent(response.data)
}
